<template>
  <div class="px-1 pt-1">
    <div class="container-fluid">
      <div class="d-flex">
        <button class="btn btn-primary btn-lg flex-fill m-3" @click="logoutUser()">
          <font-awesome-layers class="fa-1x me-2">
            <font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" />
          </font-awesome-layers>
          {{ $t('page.profile.logout') }}
        </button>
      </div>
      <div class="card mt-3 mel-glass-panel">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="staticEmail" class="form-label">{{ $t('field.generic.email') }}</label>
              <input type="email" class="form-control" id="staticEmail" disabled :value="authUser?.email">
            </div>
            <div class="col-md-6">
              <label for="staticName" class="form-label">{{ $t('field.user.fullName') }}</label>
              <input type="text" class="form-control" id="staticName" disabled :value="authUser?.name">
            </div>
          </div>
        </div>
      </div>
      <div class="card mt-3 mel-glass-panel">
        <h4 class="card-header" @click="handleHeaderClick">{{ $t('global.options', 4) }}</h4>
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div class="input-group" style="width:auto;">
              <span class="input-group-text"><font-awesome-icon :icon="['fas', 'language']" /></span>
              <select class="form-select overflow-y-hidden" style="max-width:fit-content;" id="cultureName" name="cultureName" required v-model="userSettings.cultureName">
                <option v-for="culture in selectableCultures" :key="culture.code" :value="culture.code">{{ $t(culture.nameKey) }}</option>
              </select>
            </div>
            <div class="btn-group" role="group" aria-label="Color mode">
              <input type="radio" class="btn-check" name="colorMode" id="colorModeSystem" value="system" v-model="userSettings.colorMode" autocomplete="off">
              <label class="btn btn-outline-secondary" for="colorModeSystem">
                <font-awesome-icon :icon="['fas', 'desktop']" class="me-1" />
                {{ $t('action.colorModeSystem') }}
              </label>
              <input type="radio" class="btn-check" name="colorMode" id="colorModeLight" value="light" v-model="userSettings.colorMode" autocomplete="off">
              <label class="btn btn-outline-secondary" for="colorModeLight">
                <font-awesome-icon :icon="['fas', 'sun']" class="me-1" />
                {{ $t('action.colorModeLight') }}
              </label>
              <input type="radio" class="btn-check" name="colorMode" id="colorModeDark" value="dark" v-model="userSettings.colorMode" autocomplete="off">
              <label class="btn btn-outline-secondary" for="colorModeDark">
                <font-awesome-icon :icon="['fas', 'moon']" class="me-1" />
                {{ $t('action.colorModeDark') }}
              </label>
            </div>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" role="switch" id="checkConfirmQuantity"
              :checked="userSettings.confirmQuantity" @click="toggleConfirmQuantity">
            <label class="form-check-label fw-bold me-2" for="checkConfirmQuantity">{{ $t('action.confirmQuantity') }}</label>
            <label class="form-check-label" for="checkConfirmQuantity">{{ $t('instruction.confirmQuantitySummary') }}</label>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" role="switch" id="checkShowBarcode"
              :checked="userSettings.showBarcode" @click="toggleShowBarcode">
            <label class="form-check-label fw-bold me-2" for="checkShowBarcode">{{ $t('action.showBarcode') }}</label>
            <label class="form-check-label" for="checkShowBarcode">{{ $t('instruction.showBarcodeSummary') }}</label>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" role="switch" id="checkShowAuditVisual"
              :checked="userSettings.showAuditVisual" @click="toggleShowAuditVisual">
            <label class="form-check-label fw-bold me-2" for="checkShowAuditVisual">{{ $t('action.showAuditVisual')}}</label>
            <label class="form-check-label" for="checkShowAuditVisual">{{ $t('instruction.showAuditVisualSummary') }}</label>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" role="switch" id="checkShowLotVisual"
              :checked="userSettings.showLotVisual" @click="toggleShowLotVisual">
            <label class="form-check-label fw-bold me-2" for="checkShowLotVisual">{{ $t('action.showLotVisual')}}</label>
            <label class="form-check-label" for="checkShowLotVisual">{{ $t('instruction.showLotVisualSummary') }}</label>
          </div>
          <div class="form-check form-switch" v-if="showDeveloperMode">
            <input class="form-check-input" type="checkbox" role="switch" id="checkDeveloperMode"
              :checked="userSettings.developerMode" @click="toggleDeveloperMode">
            <label class="form-check-label fw-bold me-2" for="checkDeveloperMode">{{ $t('action.developerMode') }}</label>
            <label class="form-check-label" for="checkDeveloperMode">{{ $t('instruction.developerModeSummary') }}</label>
          </div>
        </div>
      </div>
      <div class="card mt-3 mel-glass-panel">
        <h4 class="card-header">
          {{ $t('page.profile.passkeys') }}
        </h4>
        <div class="card-body">
          <div class="input-group mb-3">
            <input type="text" class="form-control" v-model="passkeyName" maxlength="20" :placeholder="$t('field.generic.name')" :aria-label="$t('field.generic.name')" aria-describedby="button-addon2">
            <button class="btn btn-primary" type="button" id="button-addon2" @click="registerAdditionalPassKey()" :disabled="!passkeyName">
              <font-awesome-icon :icon="['fas', 'plus']" />
              {{ $t('action.addPasskey') }}
            </button>
          </div>
          <div class="mel-glass-panel mel-glass-panel-inset p-3">
          <table class="table table-borderless table-sm table-hover align-middle mb-0 bg-transparent" v-if="passkeys.length > 0">
            <thead>
              <tr>
                <th scope="col">{{ $t('field.generic.name') }}</th>
                <th scope="col" class="d-none d-md-table-cell">{{ $t('field.generic.updated') }}</th>
                <th scope="col" class="d-none d-xl-table-cell">{{ $t('field.fidoCredential.lastUsedPlatformInfo') }}</th>
                <th scope="col-auto"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="passkey in passkeys" :key="passkey.id">
                <td>{{ passkey.name }}</td>
                <td class="d-none d-md-table-cell">{{ $d(new Date(passkey.updatedAt), 'dateandtime') }}</td>
                <td class="d-none d-xl-table-cell">{{ passkey.lastUsedPlatformInfo }}</td>
                <td class="col-auto btn-group d-flex flex-column align-items-end">
                  <button class="btn btn-outline-danger" type="button" @click="removePasskey(passkey.id)">
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>
      <div class="card mt-3 mel-glass-panel" v-if="features.entraId">
        <h4 class="card-header">
          {{ $t('page.profile.entraLogins') }}
        </h4>
        <div class="card-body">
          <div class="mb-3">
            <button class="btn btn-primary" type="button" @click="linkEntraAccount()" :disabled="isLinkingEntra">
              <span v-show="isLinkingEntra" class="spinner-border spinner-border-sm me-1"></span>
              <font-awesome-icon :icon="['fab', 'microsoft']" class="me-1" />
              {{ $t('action.linkMicrosoftAccount') }}
            </button>
          </div>
          <div class="mel-glass-panel mel-glass-panel-inset p-3">
            <table class="table table-borderless table-sm table-hover align-middle mb-0 bg-transparent" v-if="entraLogins.length > 0">
              <thead>
                <tr>
                  <th scope="col">{{ $t('field.generic.email') }}</th>
                  <th scope="col" class="d-none d-md-table-cell">{{ $t('field.entraLogin.displayName') }}</th>
                  <th scope="col" class="d-none d-xl-table-cell">{{ $t('field.generic.created') }}</th>
                  <th scope="col-auto"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="login in entraLogins" :key="login.id">
                  <td>{{ login.email ?? '-' }}</td>
                  <td class="d-none d-md-table-cell">{{ login.displayName ?? '-' }}</td>
                  <td class="d-none d-xl-table-cell">{{ $d(new Date(login.createdAt), 'dateandtime') }}</td>
                  <td class="col-auto btn-group d-flex flex-column align-items-end">
                    <button class="btn btn-outline-danger" type="button" @click="removeEntraLogin(login.id)">
                      <font-awesome-icon :icon="['fas', 'trash']" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="mb-0 text-muted">{{ $t('page.profile.noEntraLogins') }}</p>
          </div>
        </div>
      </div>
      <div class="card mt-3 mel-glass-panel">
        <h4 class="card-header">
          {{ $t('page.profile.loginToken') }}
          <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
        </h4>
        <div class="card-body d-flex d-md-none">
          <h5>
            <font-awesome-icon :icon="['fas', 'info-circle']" />
            {{ $t('page.profile.manageThisSettingOnDesktop') }}
          </h5>
        </div>
        <div class="card-body d-none d-md-flex flex-row align-items-center justify-content-between">
          <div class="btn-group me-2">
            <button v-if="!loginToken" class="btn btn-primary" @click="generateLoginToken()">
              {{ $t('page.profile.generateLoginToken') }}
            </button>
            <button v-else @click="openQrPrintView" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'print']" />
            </button>
            <template v-if="loginToken && userSettings.developerMode">
              <input :type="showLoginToken ? 'text' : 'password'" class="form-control" :value="loginToken" style="width: 460px;">
              <button @click="showLoginToken = !showLoginToken" class="btn btn-outline-secondary">
                <font-awesome-icon v-if="!showLoginToken" :icon="['fas', 'eye']" fixed-width />
                <font-awesome-icon v-if="showLoginToken" :icon="['fas', 'eye-slash']" fixed-width />
              </button>
              <button @click="copyLoginToken()" class="btn btn-outline-secondary">
                <font-awesome-icon :icon="['fas', 'copy']" />
              </button>
            </template>
            <button class="btn btn-outline-danger" type="button" @click="removeLoginToken()">
              <font-awesome-icon :icon="['fas', 'trash']" />
              <!-- {{ $t('page.profile.removeLoginToken') }} -->
            </button>
          </div>
          <div v-if="!loginToken" class="alert alert-warning d-flex align-items-center mb-0 p-2 me-auto" role="alert">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
            <p class="mb-0">{{ $t('page.profile.loginTokenReplaceWarning') }}</p>
          </div>
          <div v-if="loginToken && userSettings.developerMode" class="p-3">
            <QrCodeDiagram :text="showLoginToken ? loginToken: ''" :size="'6rem'" />
          </div>
        </div>
      </div>
      <div class="card mt-3 mel-glass-panel" v-if="userSettings.developerMode">
        <h4 class="card-header">
          {{ $t('page.profile.generateApiKeyTitle') }}
          <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
        </h4>
        <div class="card-body d-flex d-md-none">
          <h5>
            <font-awesome-icon :icon="['fas', 'info-circle']" />
            {{ $t('page.profile.manageThisSettingOnDesktop') }}
          </h5>
        </div>
        <div class="card-body d-none d-md-flex flex-row align-items-center">
          <div class="btn-group me-2">
            <button v-if="!apiKey" class="btn btn-primary" @click="generateApiKey()">
              {{ $t('page.profile.generateApiKey') }}
            </button>
            <template v-if="apiKey">
              <input :type="showApiKey ? 'text' : 'password'" class="form-control" :value="apiKey" style="width: 300px;">
              <button @click="showApiKey = !showApiKey" class="btn btn-outline-secondary">
                <font-awesome-icon v-if="!showApiKey" :icon="['fas', 'eye']" fixed-width />
                <font-awesome-icon v-if="showApiKey" :icon="['fas', 'eye-slash']" fixed-width />
              </button>
              <button @click="copyKey()" class="btn btn-outline-secondary">
                <font-awesome-icon :icon="['fas', 'copy']" />
              </button>
            </template>
            <button class="btn btn-outline-danger" type="button" @click="removeApiKey()">
              <font-awesome-icon :icon="['fas', 'trash']" />
              <!-- {{ $t('page.profile.removeApiKey') }} -->
            </button>
          </div>
          <div class="alert alert-warning d-flex align-items-center mb-0 p-2" role="alert">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-2" />
            <p v-if="!apiKey" class="mb-0">{{ $t('page.profile.apiKeyReplaceWarning') }}</p>
            <p v-if="apiKey" class="mb-0">{{ $t('page.profile.apiKeyShownOnceWarning') }}</p>
          </div>
        </div>
      </div>
      <div v-if="selectedTenant" class="card mt-3 mel-glass-panel">
        <h4 class="card-header d-flex justify-content-between align-items-center">
          <span>
            {{ $t('page.profile.linkedServicesTitle') }}
            <span class="mx-1 text-muted">|</span>
            <span class="text-primary">{{ selectedTenant.name }}</span>
          </span>
          <div class="dropdown" v-if="hasUnconfiguredServices">
            <button class="btn btn-primary btn-sm dropdown-toggle" type="button" id="addServiceDropdown" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t('page.profile.addLinkedService') }}
            </button>
            <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="addServiceDropdown">
              <li v-if="!linkedServices.some(s => s.linkedServiceId === AXENITA_GUID)">
                <a class="dropdown-item" href="#" @click.prevent="addAxenitaService()">
                  <img src="/linked-service/logo-axenita-light.svg" alt="Axenita Logo" style="height: 24px; width: auto; margin-right: 0.5rem;" />
                </a>
              </li>
              <!-- <li v-if="!linkedServices.some(s => s.linkedServiceId === SAMPLE_GUID)">
                <a class="dropdown-item" href="#" @click.prevent="addSampleService()">
                  <img src="/linked-service/unknown.svg" alt="Unknown Logo" style="height: 24px; width: auto; margin-right: 0.5rem;" />
                </a>
              </li> -->
            </ul>
          </div>
        </h4>
        <div class="card-body d-flex d-md-none">
          <h5>
            <font-awesome-icon :icon="['fas', 'info-circle']" />
            {{ $t('page.profile.manageThisSettingOnDesktop') }}
          </h5>
        </div>
        <div class="card-body d-none d-md-flex">
          <div v-if="linkedServicesLoading" class="spinner-border spinner-border-sm"></div>
          <div v-else v-for="service in linkedServices" :key="service.id" class="mel-glass-panel mel-glass-panel-inset p-3 mt-2 flex-grow-1">
            <div class="mb-2 gap-4 d-flex align-items-center">
              <img v-if="service.linkedServiceId === AXENITA_GUID" src="/linked-service/logo-axenita-light.svg" alt="Axenita Logo" style="height: 70px; width: auto; margin-right: 0.5rem;" />
              <img v-else-if="service.linkedServiceId === SAMPLE_GUID" src="/linked-service/unknown.svg" alt="Generic Logo" style="height: 70px; width: auto; margin-right: 0.5rem;" />
              <div class="flex-grow-1">
                <label class="form-label">{{ $t('field.serviceConnection.endpointURL') }}</label>
                <input v-model="service.endpointUrl" class="form-control" type="text" />
              </div>
              <button class="btn btn-success me-2 align-self-start" @click="saveLinkedService(service)">
                <font-awesome-icon :icon="['fas', 'save']" />
                {{ $t('action.save') }}
              </button>
              <button v-if="service.id && service.linkedServiceId === AXENITA_GUID" class="btn btn-outline-info me-2 align-self-start" @click="verifyLinkedServiceConnection(service)">
                <font-awesome-icon :icon="['fas', 'cloud-question']" />
                {{ $t('action.verifyConnection') }}
              </button>
              <div class="btn-group d-flex flex-column align-items-end align-self-start">
                <button class="btn btn-outline-danger" @click="deleteLinkedService(service)">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
            <div class="row g-2 align-items-center">
              <template v-if="service.credentials.authType === AUTH_TYPE.basic">
              <div class="col-md-3">
                <label class="form-label">{{ $t('field.credential.username') }}</label>
                <input v-model="(service.credentials as BasicCredential).username" class="form-control" type="text" autocomplete="off" />
              </div>
              <div class="col-md-3">
                <label class="form-label">{{ $t('field.credential.password') }}</label>
                <input v-model="(service.credentials as BasicCredential).password" class="form-control" type="password" autocomplete="off" />
              </div>
              <div class="col-md-6">
                <div :key="(service.credentials as BasicTimeBasedCredential).authenticatorCode">
                  <label class="form-label">
                    {{ $t('field.credential.authenticatorSecret') }}
                    <span name="authCode" class="text-melodi ms-2">{{ (service.credentials as BasicTimeBasedCredential).authenticatorCode }}</span>
                  </label>
                </div>
                <input v-model="(service.credentials as BasicTimeBasedCredential).oneTimeAccount" class="form-control" type="password" autocomplete="off" />
              </div>
              </template>
              <template v-if="service.credentials.authType === AUTH_TYPE.basicTimeBased">
              <div class="col-md-3">
                <label class="form-label">{{ $t('field.credential.username') }}</label>
                <input v-model="(service.credentials as BasicTimeBasedCredential).username" class="form-control" type="text" autocomplete="off" />
              </div>
              <div class="col-md-3">
                <label class="form-label">{{ $t('field.credential.password') }}</label>
                <input v-model="(service.credentials as BasicTimeBasedCredential).password" class="form-control" type="password" autocomplete="off" />
              </div>
              <div class="col-md-6">
                <div :key="(service.credentials as BasicTimeBasedCredential).authenticatorCode">
                  <label class="form-label">
                    {{ $t('field.credential.authenticatorSecret') }}
                    <span name="authCode" class="text-melodi ms-2">{{ (service.credentials as BasicTimeBasedCredential).authenticatorCode }}</span>
                    <span v-if="(service.credentials as BasicTimeBasedCredential).oneTimeAccountSecret">
                      <div class="progress mt-1" role="progressbar" aria-valuemin="0" aria-valuemax="100" style="height:6px;">
                        <div class="progress-bar mel-totp-progress" :style="getTotpProgressStyle()"></div>
                      </div>
                    </span>
                  </label>
                </div>
                <input v-model="(service.credentials as BasicTimeBasedCredential).oneTimeAccount" class="form-control" type="password" autocomplete="off" />
              </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-3"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { logoutUser } from '@/services/auth';
import { useAuthStore, useUserSettingsStore, usePasskeysStore, useUserLinkedServicesStore, useSessionStore, useEntraLoginsStore, useFeaturesStore } from '@/stores';
import { Id, toast, type ToastOptions } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import { passKeyService } from '@/services/passkey-service';
import api from '@/services/api';
import TranslationsService from '@/services/translations';
import { AxenitaUpsertUserLinkedServiceRequest } from '@/models/requests/UpsertUserLinkedServiceRequest';
import type IEntraAuthorizeResponse from '@/models/responses/IEntraAuthorizeResponse';
import { onMounted, onUnmounted } from 'vue';
import { BasicCredential, BasicTimeBasedCredential, UserLinkedService } from '@/models/responses/IUserLinkedService';
import { AUTH_TYPE } from '@/models/Enums';
import { TOTP } from "totp-generator";
import QrCodeDiagram from '@/components/diagram/QrCode.vue';
import QRCode from 'qrcode';

const userSettingsStore = useUserSettingsStore();
const authStore = useAuthStore();
const sessionStore = useSessionStore();
const passkeysStore = usePasskeysStore();
const userLinkedServicesStore = useUserLinkedServicesStore();
const entraLoginsStore = useEntraLoginsStore();
const featuresStore = useFeaturesStore();

const { data: userSettings } = storeToRefs(userSettingsStore);
const { user: authUser } = storeToRefs(authStore);
const { selectedTenant, selectedTenantId } = storeToRefs(sessionStore);
const { data: passkeys } = storeToRefs(passkeysStore);
const { data: entraLogins } = storeToRefs(entraLoginsStore);
const { data: features } = storeToRefs(featuresStore);
const { error: userLinkedServicesStoreError } = storeToRefs(userLinkedServicesStore);

const i18n = useI18n();

const isSaving = ref(false);
const toastId = ref('' as Id);

const apiKey = ref('');
const showApiKey = ref(false);
const loginToken = ref('');
const showLoginToken = ref(false);
const passkeyName = ref('');
const headerClickCount = ref(0);
const isLinkingEntra = ref(false);
const viewportSize = ref({ width: window.innerWidth, height: window.innerHeight });

let authenticatorInterval: ReturnType<typeof setInterval> | undefined;
let authenticatorTimeout: ReturnType<typeof setTimeout> | undefined;

const selectableCultures = computed(() => {
  const cultures = [
    { code: 'en', nameKey: 'language.en' },
    { code: 'de', nameKey: 'language.de' },
    { code: 'fr', nameKey: 'language.fr' }
  ];
  return cultures.sort((a, b) => i18n.t(a.nameKey).localeCompare(i18n.t(b.nameKey), i18n.locale.value));
});

const onCultureNameChange = async () => {
  await userSettingsStore.postUserSettings();
  // Optionally reload or update app language if needed
};

onMounted(async () => {
  await loadLinkedServices();
  if (features.value.entraId) {
    await entraLoginsStore.fetchAll();
  }
  await recalculateAllAuthenticatorCodes();
  scheduleAuthenticatorAligned();
  window.addEventListener('resize', recalculateViewportSize);
});

onUnmounted(() => {
  if (authenticatorInterval) {
    clearInterval(authenticatorInterval);
  }
  authenticatorInterval = undefined;
  if (authenticatorTimeout) {
    clearTimeout(authenticatorTimeout);
  }
  authenticatorTimeout = undefined;
  window.removeEventListener('resize', recalculateViewportSize);
});

const recalculateViewportSize = () => {
  viewportSize.value = {
    width: window.innerWidth,
    height: window.innerHeight
  };
};

const recalculateAllAuthenticatorCodes = async () => {
  for (const service of linkedServices.value) {
    if (service.credentials.authType === AUTH_TYPE.basicTimeBased) {
      const cred = service.credentials as BasicTimeBasedCredential;
      if (cred.oneTimeAccountSecret) {
        try {
          const { otp, expires } = await TOTP.generate(cred.oneTimeAccountSecret, {
            digits: 6,
            algorithm: "SHA-1",
            period: 30,
          });
          cred.authenticatorCode = otp;
          const expiresAtString = i18n.d(new Date(expires), 'exacttimeonly');
          console.debug(`Calculated TOTP for service ${service.linkedServiceId}: ${cred.authenticatorCode}, expires at ${expiresAtString}`);
        } catch (error) {
          console.error('Error calculating TOTP:', error);
        }
      }
      else {
        cred.authenticatorCode = '';
        console.debug(`No oneTimeAccountSecret for service ${service.linkedServiceId}, skipping TOTP calculation.`);
      }
    }
  }
};

// Align authenticator recalculation ticks to wall-clock :00 and :30 seconds
function scheduleAuthenticatorAligned() {
  if (authenticatorInterval) {
    clearInterval(authenticatorInterval);
    authenticatorInterval = undefined;
  }
  if (authenticatorTimeout) {
    clearTimeout(authenticatorTimeout);
    authenticatorTimeout = undefined;
  }

  const now = Date.now();
  const msIntoMinute = now % 60000;
  const delay = msIntoMinute < 30000 ? (30000 - msIntoMinute) : (60000 - msIntoMinute);

  authenticatorTimeout = setTimeout(async () => {
    await recalculateAllAuthenticatorCodes();
    authenticatorInterval = setInterval(async () => {
      await recalculateAllAuthenticatorCodes();
    }, 30000);
  }, delay);
}

// Returns a style object for a 30s progress animation aligned to wall-clock time
const getTotpProgressStyle = () => {
  const now = Date.now();
  const elapsedSeconds = (now % 30000) / 1000; // seconds into current 30s window
  return {
    animationDelay: `-${elapsedSeconds}s`,
  } as Record<string, string>;
};

// Watch for culture changes
watch(() => userSettings.value.cultureName, async (newValue) => {
  if (newValue) {
    await onCultureNameChange();
    await TranslationsService.changeLocale(newValue);
  }
});

// Watch for color mode changes
watch(() => userSettings.value.colorMode, async () => {
  await userSettingsStore.postUserSettings();
});

const toggleDeveloperMode = async () => {
  userSettings.value.developerMode = !userSettings.value.developerMode;
  await userSettingsStore.postUserSettings();
};

const toggleConfirmQuantity = async () => {
  userSettings.value.confirmQuantity = !userSettings.value.confirmQuantity;
  await userSettingsStore.postUserSettings();
};

const toggleShowAuditVisual = async () => {
  userSettings.value.showAuditVisual = !userSettings.value.showAuditVisual;
  await userSettingsStore.postUserSettings();
};

const toggleShowLotVisual = async () => {
  userSettings.value.showLotVisual = !userSettings.value.showLotVisual;
  await userSettingsStore.postUserSettings();
};

const toggleShowBarcode = async () => {
  userSettings.value.showBarcode = !userSettings.value.showBarcode;
  await userSettingsStore.postUserSettings();
};

const generateApiKey = async () => {
  isSaving.value = true;
  toastId.value = toast.loading(i18n.t('page.profile.apiKeyCreating'), {
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  await api.put("v1.0/users/.self/apikey", {
  }).then((response) => {
    apiKey.value = response.data as string;
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.apiKeyCreated'),
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 3000,
      closeOnClick: true
    } as ToastOptions);
  }).catch((error) => {
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.apiKeyCreateError', { error: error.message }),
      type: toast.TYPE.ERROR,
      closeOnClick: true,
      isLoading: false
    } as ToastOptions);
  });
}

const removeApiKey = async () => {
  isSaving.value = true;
  toastId.value = toast.loading(i18n.t('page.profile.apiKeyDeleting'), {
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  await api.delete("v1.0/users/.self/apikey").then(() => {
    apiKey.value = '';
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.apiKeyDeleted'),
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 3000,
      closeOnClick: true
    } as ToastOptions);
  }).catch((error) => {
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.apiKeyDeleteError', { error: error.message }),
      type: toast.TYPE.ERROR,
      closeOnClick: true,
      isLoading: false
    } as ToastOptions);
  });
}

const generateLoginToken = async () => {
  isSaving.value = true;
  toastId.value = toast.loading(i18n.t('page.profile.loginTokenCreating'), {
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  await api.put("v1.0/users/.self/token", {
  }).then(async (response) => {
    loginToken.value = response.data as string;
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.loginTokenCreated'),
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 3000,
      closeOnClick: true
    } as ToastOptions);
    await openQrPrintView();
  }).catch((error) => {
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.loginTokenCreateError', { error: error.message }),
      type: toast.TYPE.ERROR,
      closeOnClick: true,
      isLoading: false
    } as ToastOptions);
  });
}

const removeLoginToken = async () => {
  isSaving.value = true;
  toastId.value = toast.loading(i18n.t('page.profile.loginTokenDeleting'), {
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  await api.delete("v1.0/users/.self/token").then(() => {
    loginToken.value = '';
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.loginTokenDeleted'),
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 3000,
      closeOnClick: true
    } as ToastOptions);
  }).catch((error) => {
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('page.profile.loginTokenDeleteError', { error: error.message }),
      type: toast.TYPE.ERROR,
      closeOnClick: true,
      isLoading: false
    } as ToastOptions);
  });
}

const copyKey = () => {
  copyToClipboardWithMessage(apiKey.value, i18n.t('page.profile.apiKeyCopied'));
}

const copyLoginToken = () => {
  copyToClipboardWithMessage(loginToken.value, i18n.t('page.profile.loginTokenCopied'));
}

function copyToClipboardWithMessage(clipboardContent: string, message: string) {
  navigator.clipboard.writeText(clipboardContent).then(
    () => toast.success(message, { autoClose: 3000 }),
    () => toast.error(i18n.t('page.profile.apiKeyCopiedError'), { autoClose: 3000 })
  );
}

async function registerAdditionalPassKey() {
  var abortController = new AbortController();
  try {
    const makeCredentialOptions = await passKeyService.createCredentialOptions();
    await passKeyService.createCredential(passkeyName.value, makeCredentialOptions);
    passkeysStore.fetchAll(true);
    passkeyName.value = '';
  } catch (e: any) {
    toast.error(e, { autoClose: 3000 });
  }
}

const removePasskey = async (id: number) => {
  await passkeysStore.delete(id);
}

const linkEntraAccount = async () => {
  isLinkingEntra.value = true;
  try {
    const response = await api.get<IEntraAuthorizeResponse>('v1.0/auth-entra/authorize');
    window.location.href = response.data.authorizationUrl;
  } catch (e: any) {
    isLinkingEntra.value = false;
    toast.error(e?.response?.data || e?.message || i18n.t('page.profile.entraLinkError'), { autoClose: 3000 });
  }
}

const removeEntraLogin = async (id: number) => {
  await entraLoginsStore.delete(id);
}

// guids have to match the linked service IDs in the backend (LinkedServiceIds)
const AXENITA_GUID = '09cfe7f9-cdc0-44d7-9664-cc28b2551392';
const SAMPLE_GUID = 'a8d422e5-d492-4ac2-9cea-a528ea26a701';

const linkedServices = ref<UserLinkedService[]>([]);
const linkedServicesLoading = ref(false);
const originalLinkedServices = ref<UserLinkedService[]>([]);

async function loadLinkedServices() {
  linkedServicesLoading.value = true;
  try {
    await userLinkedServicesStore.fetchAll(true);
    const all = userLinkedServicesStore.services;
    linkedServices.value = all.filter(s => [AXENITA_GUID, SAMPLE_GUID].includes(s.linkedServiceId));
    originalLinkedServices.value = JSON.parse(JSON.stringify(linkedServices.value));
  } finally {
    linkedServicesLoading.value = false;
  }
}

// const hasUnconfiguredServices = computed(() =>
//   !linkedServices.value.some(s => s.linkedServiceId == AXENITA_GUID) ||
//   !linkedServices.value.some(s => s.linkedServiceId == SAMPLE_GUID));

const hasUnconfiguredServices = computed(() =>
  !linkedServices.value.some(s => s.linkedServiceId == AXENITA_GUID));

const showDeveloperMode = computed(() => 
  userSettings.value.developerMode || headerClickCount.value >= 7);

const handleHeaderClick = () => {
  headerClickCount.value++;
};

// const hasServiceChanges = (service: UserLinkedService): boolean => {
//   const original = originalLinkedServices.value.find(s => s.id === service.id);
//   return JSON.stringify(service) !== JSON.stringify(original);
// };

async function saveLinkedService(service: UserLinkedService) {
  try {
    // if we have a basic credential with a one-time account, convert to basic time-based
    if (service.credentials.authType === AUTH_TYPE.basic) {
      const otpCred = service.credentials as BasicTimeBasedCredential;
      if (otpCred.oneTimeAccount) {
        service.credentials = new BasicTimeBasedCredential(otpCred.username, otpCred.password, otpCred.oneTimeAccount, otpCred.oneTimeAccountSecret);
      }
    }
    // if we have a basic time-based credential without a one-time account, convert to basic
    else if (service.credentials.authType === AUTH_TYPE.basicTimeBased) {
      const cred = service.credentials as BasicTimeBasedCredential;
      if (!cred.oneTimeAccount) {
        service.credentials = new BasicCredential(cred.username, cred.password);
      }
    }
    const upsertRequest = new AxenitaUpsertUserLinkedServiceRequest(service.tenantId, service.linkedServiceId, service.endpointUrl, service.credentials);
    await userLinkedServicesStore.upsert(upsertRequest);
    if (userLinkedServicesStoreError.value) {
      toast.error(userLinkedServicesStoreError.value, { theme: 'colored', autoClose: 2000 } as ToastOptions);
      return;
    }
    toast.success(i18n.t('page.profile.serviceLinkSaved'), { autoClose: 2000 });
    await loadLinkedServices();
  } catch (e: any) {
    toast.error(e.message || i18n.t('page.profile.failedToSave'), { autoClose: 3000 });
  }
}

async function deleteLinkedService(service: UserLinkedService) {
  try {
    await userLinkedServicesStore.delete(service.id);
    toast.success(i18n.t('page.profile.serviceLinkDeleted'), { autoClose: 2000 });
    if (userLinkedServicesStoreError.value) {
      toast.error(userLinkedServicesStoreError.value, { theme: 'colored', autoClose: 2000 } as ToastOptions);
      return;
    }
    await loadLinkedServices();
  } catch (e: any) {
    toast.error(e.message || i18n.t('page.profile.failedToDelete'), { autoClose: 3000 });
  }
}

async function verifyLinkedServiceConnection(service: UserLinkedService): Promise<boolean> {
  if (!service || !service.id || service.linkedServiceId !== AXENITA_GUID) {
    toast.error(i18n.t('page.profile.invalidServiceConfiguration'), { autoClose: 3000 });
    return false;
  }

  try {
    const toastId = toast.loading(i18n.t('page.profile.verifyingConnection'), { autoClose: false, theme: 'colored' } as ToastOptions);

    const response = await api.get(`v1.0/user-linked-services/${service.id}/.verify`);

    const isSuccessful = response.data === true;

    toast.update(toastId, {
      render: isSuccessful ? i18n.t('page.profile.connectionSuccessful') : i18n.t('page.profile.connectionFailed'),
      type: isSuccessful ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
      isLoading: false,
      autoClose: 3000,
      closeOnClick: true
    } as ToastOptions);

    return isSuccessful;
  } catch (e: any) {
    toast.error(e.message || i18n.t('page.profile.failedToVerifyConnection'), { autoClose: 3000 });
    return false;
  }
}

function addAxenitaService() {
  linkedServices.value.push({
    id: 0, // will be set by backend
    tenantId: selectedTenantId.value,
    userId: 0, // will be set by backend
    linkedServiceId: AXENITA_GUID,
    endpointUrl: '',
    credentials: new BasicTimeBasedCredential('', '', '', '')
  });
}

const openQrPrintView = async () => {
  if (!loginToken.value) return;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    try {
      // Generate QR code data URL
      const qrDataUrl = await QRCode.toDataURL(loginToken.value, {
        width: 120,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      printWindow.document.open();
      printWindow.document.title = i18n.t('page.profile.loginToken') as string;

      // Create the HTML content for printing
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${i18n.t('page.profile.loginToken')}</title>
          <style>
            body { 
              display: flex; 
              flex-direction: column;
              justify-content: center; 
              align-items: center; 
              height: 100vh; 
              margin: 0;
              font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
            }
            h1 {
              margin-bottom: 8px;
              text-align: center;
            }
            h2 {
              margin-bottom: 10px;
              text-align: center;
            }
            img { 
              max-width: 100%; 
              height: auto; 
              border: 1px solid #ccc;
              border-radius: 6px;
              padding: 12px;
              background: white;
            }
            @media print {
              body { height: auto; }
              h1 { page-break-inside: avoid; }
              h2 { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>${i18n.t('page.profile.loginToken')}</h1>
          <h2>${authUser.value?.name ?? ''}</h2>
          <img src="${qrDataUrl}" alt="Login Token QR Code" />
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for the content to load before printing
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      };
      
    } catch (error) {
      console.error('Error generating QR code for print:', error);
      toast.error(i18n.t('page.profile.printError'), { autoClose: 3000 });
      printWindow.close();
    }
  } else {
    toast.error(i18n.t('page.profile.printError'), { autoClose: 3000 });
  }
};

// function addSampleService() {
//   linkedServices.value.push({
//     id: 0,
//     linkedServiceId: SAMPLE_GUID,
//     endpointUrl: '',
//     credentials: new BasicTimeBasedCredential('', '', '', '')
//   });
// }
</script>

<style lang="scss" scoped>
.table th,
.table td {
  background-color: transparent !important;
}

.card-header {
  cursor: default;
  user-select: none;
}

.mel-totp-progress {
  width: 100%;
  animation: width-x-100-to-0 30s linear infinite;
}

@keyframes width-x-100-to-0 {
  from { width: 100%; }
  to { width: 0%; }
}
</style>
